/* eslint jsx-a11y/anchor-is-valid: 0 */
import React, { useContext, useState } from 'react'
import { Link, Redirect, Route, withRouter, Switch } from 'react-router-dom'
import { slide as Menu, State } from 'react-burger-menu'

// lib
import { AuthContext, authClient } from 'auth'

/* Assets */
import Calendar from 'images/calendar.svg'
import Qr from 'images/qr-code.svg'
import Requests from 'images/request.svg'
import Location from 'images/location.svg'

/* Constants */
import { ROUTES, LABELS } from '../lib/constants'

/* Components */
import { BurnPage } from './BurnPage'
import { IssueForEventPage, IssueForUserPage } from './IssuePage'
import { AddressManagementPage } from './AddressManagementPage'
import { TransactionsPage } from './TransactionsPage'
import { QrPage } from './QrPage'
import { QrRequests } from './QrRequests'
import { DropRequests } from './DropRequests'
import { GpsPetitions } from './GpsPetitions'
import { Drafts } from './Drafts'
import DraftReviewsPage from 'backoffice/components/DropRequests/DraftReviewsPage'
import { EventsPage } from './EventsPage'
import { Deliveries } from './Deliveries'
import { DeliveriesRequests } from './Deliveries/DeliveriesRequests'
import { WebsitesManage } from './Websites/WebsitesManage'
import { SecretsManage } from './Secrets/SecretsManage'
import Layout from './components/Layout/Layout'
import { Helmet } from 'react-helmet'
import { analyticsTracking } from 'lib/analytics/analytics'

export const MintersPage = () => <div> This is a MintersPage </div>

type RouteProps = {
  path: string
  roles?: string[]
  title?: string
}

type LabelProps = {
  roles: string[]
  title: string
}

const Label: React.FC<{ label: LabelProps }> = ({ label }) => {
  const { title } = label
  return <h2>{title}</h2>
}

const SidebarLink: React.FC<{ route: RouteProps; handleClick: () => void }> = ({ route, handleClick }) => {
  const { path, title } = route
  const onClickLink = () => {
    analyticsTracking.navigation.trackMenuLink(title || 'undefined')
    handleClick()
  }
  if (typeof route === 'object' && title) {
    return (
      <Link className={'bm-item'} to={path} data-test-id="menu-item" onClick={onClickLink}>
        {title}
      </Link>
    )
  }

  return null
}

export const withAuthentication = <T extends Object>(WrappedComponent: React.ComponentType<T>): React.FC<T> => {
  return (props: T) => {
    const isAuthenticated = authClient.isAuthenticated()

    if (!isAuthenticated) return <Redirect to="/backoffice" />

    return <WrappedComponent {...props} />
  }
}

export const NavigationMenu = withRouter(({ history }) => {
  const [isOpen, setIsOpen] = useState(false)
  const auth = useContext(AuthContext)

  const closeMenu = () => setIsOpen(false)

  const isAdmin = authClient.isAuthenticated()

  const menuStateChange = (state: State): void => {
    setIsOpen(state.isOpen)
    analyticsTracking.navigation.trackMenuOpen(state.isOpen)
  }

  return (
    <Menu isOpen={isOpen} onStateChange={(state) => menuStateChange(state)} right disableAutoFocus>
      {isAdmin && (
        <>
          <Label label={LABELS.issueBadges} />
          <SidebarLink route={ROUTES.issueForEvent} handleClick={closeMenu} />

          <SidebarLink route={ROUTES.issueForUser} handleClick={closeMenu} />

          <Label label={LABELS.otherTasks} />

          <SidebarLink route={ROUTES.addressManagement} handleClick={closeMenu} />

          <SidebarLink route={ROUTES.burn} handleClick={closeMenu} />

          <SidebarLink route={ROUTES.transactions} handleClick={closeMenu} />

          <SidebarLink route={ROUTES.adminLogs} handleClick={closeMenu} />

          <SidebarLink route={ROUTES.codes} handleClick={closeMenu} />
        </>
      )}

      <SidebarLink route={ROUTES.websites.websitesManage} handleClick={closeMenu} />

      <SidebarLink route={ROUTES.secret.secretsManage} handleClick={closeMenu} />

      <SidebarLink route={ROUTES.events} handleClick={closeMenu} />

      <SidebarLink route={ROUTES.deliveries.admin} handleClick={closeMenu} />

      {isAdmin && <SidebarLink route={ROUTES.dropRequests} handleClick={closeMenu} />}

      {isAdmin && <SidebarLink route={ROUTES.gpsPetitions} handleClick={closeMenu} />}

      {isAdmin && <SidebarLink route={ROUTES.drafts} handleClick={closeMenu} />}

      {!isAdmin && <SidebarLink route={ROUTES.adminLogin} handleClick={closeMenu} />}

      {isAdmin && (
        <a
          className="bm-item"
          href=""
          onClick={() => {
            analyticsTracking.generics.trackUserLogin(false)
            auth.logout()
          }}
        >
          Logout
        </a>
      )}
    </Menu>
  )
})

const Landing = () => {
  const isAdmin = authClient.isAuthenticated()
  return (
    <div data-test-id="landing-cards-container" className={'cards-container'}>
      <Helmet>
        <title>POAP - Back Office Landing</title>
        <meta property="og:title" content="POAP - Back Office" />
      </Helmet>
      <Link to={ROUTES.events.path} className={'card card-link'}>
        <h3>Manage Drops</h3>
        <img className={'icon'} src={Calendar} alt={'Manage Events'} />
      </Link>
      {isAdmin && (
        <>
          <Link to={ROUTES.codes.path} className={'card card-link'}>
            <h3>Manage Mint Links</h3>
            <img className={'icon'} src={Qr} alt={'Manage QR Codes'} />
          </Link>
          <Link to={ROUTES.dropRequests.path} className={'card card-link'}>
            <h3>Manage Petitions</h3>
            <img className={'icon'} src={Requests} alt={'Manage Petitions'} />
          </Link>
          <Link to={ROUTES.gpsPetitions.path} className={'card card-link'}>
            <h3>Manage Location Petitions</h3>
            <img className={'icon'} src={Location} alt={'Manage GPS Petitions'} />
          </Link>
          <Link to={ROUTES.drafts.path} className={'card card-link'}>
            <h3>Console petitions</h3>
            <img className={'icon'} src={Requests} alt={'Console petitions'} />
          </Link>
        </>
      )}
    </div>
  )
}

const IssueForEventPageWithAuthentication = withAuthentication(IssueForEventPage)
const IssueForUserPageWithAuthentication = withAuthentication(IssueForUserPage)
const TransactionsPageWithAuthentication = withAuthentication(TransactionsPage)
const QrRequestsWithAuthentication = withAuthentication(QrRequests)
const DropRequestsWithAuthentication = withAuthentication(DropRequests)
const DraftReviewsPageWithAuthentication = withAuthentication(DraftReviewsPage)
const GpsPetitionsWithAuthentication = withAuthentication(GpsPetitions)
const DraftsWithAuthentication = withAuthentication(Drafts)
const DeliveriesRequestsWithAuthentication = withAuthentication(DeliveriesRequests)
const MintersPageWithAuthentication = withAuthentication(MintersPage)
const BurnPageWithAuthentication = withAuthentication(BurnPage)
const AddressManagementPageWithAuthentication = withAuthentication(AddressManagementPage)
const MintLinksPageWithAuthentication = withAuthentication(QrPage)

export const BackOffice: React.FC = () => (
  <>
    {/* @ts-ignore TODO: remove ignore TS validation */}
    <Layout>
      <Switch>
        <Route exact path={ROUTES.codes.path} render={() => <MintLinksPageWithAuthentication />} />

        <Route exact path={ROUTES.dropRequests.path} render={() => <DropRequestsWithAuthentication />} />

        <Route
          exact
          path={ROUTES.draftReviews.path}
          render={(props) => <DraftReviewsPageWithAuthentication {...props} />}
        />

        <Route exact path={ROUTES.gpsPetitions.path} render={() => <GpsPetitionsWithAuthentication />} />

        <Route exact path={ROUTES.drafts.path} render={() => <DraftsWithAuthentication />} />

        <Route exact path={ROUTES.codesRequest.path} render={() => <QrRequestsWithAuthentication />} />

        <Route exact path={ROUTES.deliveries.requests.path} render={() => <DeliveriesRequestsWithAuthentication />} />

        <Route path={ROUTES.events.path} render={() => <EventsPage />} />

        <Route exact path={ROUTES.admin} render={() => <Landing />} />

        <Route exact path={ROUTES.issueForEvent.path} render={() => <IssueForEventPageWithAuthentication />} />

        <Route exact path={ROUTES.issueForUser.path} render={() => <IssueForUserPageWithAuthentication />} />

        <Route exact path={ROUTES.minters.path} render={() => <MintersPageWithAuthentication />} />

        <Route exact path={ROUTES.burn.path} render={() => <BurnPageWithAuthentication />} />

        <Route exact path={ROUTES.burnToken.path} render={() => <BurnPageWithAuthentication />} />

        <Route exact path={ROUTES.addressManagement.path} render={() => <AddressManagementPageWithAuthentication />} />

        <Route exact path={ROUTES.transactions.path} render={() => <TransactionsPageWithAuthentication />} />

        <Route path={ROUTES.deliveries.admin.path} render={() => <Deliveries />} />

        <Route path={ROUTES.websites.websitesManage.path} render={() => <WebsitesManage />} />

        <Route path={ROUTES.secret.secretsManage.path} render={() => <SecretsManage />} />

        <Route path="*" render={() => <Redirect to="/backoffice" />} />
      </Switch>
    </Layout>
  </>
)
